import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, type PostValue } from './client'

/**
 * Settings -> Companies, sectiunea COMPANY SETTINGS, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Companies.php`:
 *
 *   index                          -> GET  /settings/companies           (bara)
 *   my_company                     -> GET  /settings/companies/information
 *   suport                         -> POST /settings/companies/information/support
 *   contacts                       -> GET  /settings/companies/contacts
 *   update_main / _financiar / _administrativ
 *                                  -> POST /settings/companies/contacts   (`role`)
 *   groups / groupsData            -> GET  /settings/companies/groups
 *   add_group / update_group       -> POST /settings/companies/groups
 *   deleteGroup                    -> POST /settings/companies/groups/{id}/remove
 *   owners / ownersData            -> GET  /settings/companies/owners
 *   deleteOwner                    -> POST /settings/companies/owners/{id}/remove
 *   companyEvents / user_events_data
 *                                  -> GET  /settings/companies/events
 *   adam_intop_companies           -> GET  /settings/companies/adam_intop
 *   save_adam_intop_assign_company -> POST /settings/companies/adam_intop
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * TREI NIVELURI DE MENIU. Bara din stanga (`tabs` din `shell`), filele
 * dinauntrul lui "Information" si "Contacts" (`tabs` din raspunsul lor), si
 * atat: sub ele e o lista sau un formular. Toate trei vin FILTRATE PE DREPTURI
 * DE PE SERVER - ecranul nu stie ce drepturi are omul si nu ascunde nimic
 * singur.
 *
 * CELE CINCI INCARCARI DE IMAGINE (sigla firmei, sigla si iconita agentului,
 * sigla si fundalul de EaseeHelp) AU RAMAS IN PORTAL: trei din ele micsoreaza
 * imaginea cu o biblioteca pe care v4 nu o are. Ecranul le ARATA (adresele vin
 * de la server) si trimite la `classicUrl` ca sa se schimbe.
 */

/** O fila din bara din stanga. */
export type CompanyTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  group: string
}

export type CompanyShell = {
  pageTitle: string
  moduleUrl: string
  /** DEJA filtrate pe server. */
  tabs: CompanyTab[]
  companyId: number
}

/** Raspunsul unei liste, in forma pe care o astepta DataTables. */
export type TablePage<T> = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export const TABS = ['my_company', 'contacts', 'groups', 'owners', 'companyEvents', 'adam_intop_companies'] as const

export type CompanyTabId = (typeof TABS)[number]

export function isCompanyTab(value: string): value is CompanyTabId {
  return (TABS as readonly string[]).includes(value)
}

export const companyShellQuery = queryOptions({
  queryKey: ['settings', 'companies', 'shell'],
  queryFn: () => apiGet<CompanyShell>('/settings/companies'),
})

/* -------------------------------------------------------------------------
 * Information (my_company.php)
 * ---------------------------------------------------------------------- */

/**
 * O fila dinauntrul lui "Information".
 *
 * `upload` spune ca fila are o incarcare ramasa in portal; `url` e adresa
 * imaginii de acum, goala cand serverul de descarcari nu e configurat.
 */
export type InformationTab = {
  id: string
  label: string
  image: string
  upload: boolean
  right: string
  url: string
}

export type CompanyInformation = {
  tabs: InformationTab[]
  companyName: string
  /** `firma.agent_logo_link` - "Current link url:" din fila "Agent icon". */
  agentLogoLink: string
  supportPhone: string
  supportEmail: string
  imagesReady: boolean
  /** Ecranul vechi, unde au ramas cele cinci incarcari. */
  classicUrl: string
}

export const companyInformationQuery = queryOptions({
  queryKey: ['settings', 'companies', 'information'],
  queryFn: () => apiGet<CompanyInformation>('/settings/companies/information'),
})

/**
 * Cele cinci incarcari de imagine (`company_logo`, `agent_logo`, `agent_ico`,
 * `easeehelp_logo`, `easeehelp_bg`) - `POST /settings/companies/information/images/{fila}`,
 * `multipart/form-data`, cu fisierul in campul lui vechi (`company_logo`,
 * `optimum_logo`, `agent_ico`, `easee_logo`, `easee_bg`). Raspunsul aduce
 * adresa noua a imaginii.
 */
export const IMAGE_FIELDS: Record<string, string> = {
  company_logo: 'company_logo',
  agent_logo: 'optimum_logo',
  agent_icon: 'agent_ico',
  easeehelp_logo: 'easee_logo',
  service_provider: 'easee_bg',
}

export function uploadCompanyImage(kind: string, file: File) {
  const body = new FormData()
  body.append(IMAGE_FIELDS[kind] ?? 'file', file, file.name)
  return apiPostForm<{ saved: boolean; kind: string; url: string }>(`/settings/companies/information/images/${kind}`, body)
}

export function saveSupport(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; supportPhone: string; supportEmail: string }>(
    '/settings/companies/information/support',
    fields,
  )
}

/* -------------------------------------------------------------------------
 * Contacts (contacts.php)
 * ---------------------------------------------------------------------- */

/** O fila dinauntrul lui "Contacts", cu cine e ales acum pe ea. */
export type ContactRoleTab = {
  id: string
  label: string
  right: string
  /** 0 = nimeni ales inca. */
  employeeId: number
}

export type CompanyEmployee = {
  id: number
  label: string
}

export type CompanyContacts = {
  tabs: ContactRoleTab[]
  employees: CompanyEmployee[]
}

export const companyContactsQuery = queryOptions({
  queryKey: ['settings', 'companies', 'contacts'],
  queryFn: () => apiGet<CompanyContacts>('/settings/companies/contacts'),
})

/** Un singur capat pentru toate trei rolurile: `role` spune care. */
export function saveContactRole(role: string, employee: number) {
  return apiPost<{ saved: boolean; role: string; employeeId: number }>('/settings/companies/contacts', {
    role,
    employee,
  })
}

/* -------------------------------------------------------------------------
 * Groups
 * ---------------------------------------------------------------------- */

export type CompanyGroup = {
  id: number
  name: string
  created: string
  modified: string
  DT_RowId?: string
}

export type CompaniesTableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export function companiesTableParams(r: CompaniesTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function companyGroupsQuery(r: CompaniesTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'companies', 'groups', r],
    queryFn: () => apiGet<TablePage<CompanyGroup>>(`/settings/companies/groups?${companiesTableParams(r).toString()}`),
  })
}

/** Fara `id` creeaza (`add_group`), cu `id` redenumeste (`update_group`). */
export function saveGroup(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/companies/groups', fields)
}

export function removeGroup(id: number) {
  return apiPost<{ removed: boolean }>(`/settings/companies/groups/${id}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Device Owners
 * ---------------------------------------------------------------------- */

/**
 * Un rand din "Device Owners".
 *
 * E ACELASI rand ca in Settings -> Contacts: `ownersData` si lista de acolo
 * sunt aceeasi interogare, pe aceleasi tabele, cu acelasi drept. De-aia capatul
 * nou nu o scrie a doua oara, ci o cere de la ecranul ala.
 */
export type CompanyOwner = {
  id: number
  firstname: string
  lastname: string
  companyName: string
  email: string
  phone: string
  mobilePhone: string
  createDate: string
  lastEdit: string
  vip: number
  createdBy: string
  DT_RowId?: string
}

export function companyOwnersQuery(r: CompaniesTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'companies', 'owners', r],
    queryFn: () => apiGet<TablePage<CompanyOwner>>(`/settings/companies/owners?${companiesTableParams(r).toString()}`),
  })
}

/**
 * Scoate un detinator din firma.
 *
 * In portal, capatul asta stergea contactul ORICAREI firme - un `UPDATE ...
 * WHERE ID = ?` fara firma. Aici firma vine din token si e conditie in acelasi
 * UPDATE, deci un ID strain raspunde 404.
 */
export function removeOwner(id: number) {
  return apiPost<{ removed: boolean }>(`/settings/companies/owners/${id}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Company Events
 * ---------------------------------------------------------------------- */

/**
 * Un rand din "Company Events".
 *
 * Numele filei e mostenit: `user_events_data()` aducea jurnalul CONTULUI CELUI
 * LOGAT (`it_user_accounts_events` n-are coloana de firma). Se arata acelasi
 * lucru ca in portal.
 */
export type CompanyEvent = {
  id: number
  date: string
  message: string
  /** Gol = randul n-are om in spate; cuvantul "System" il scrie ecranul. */
  user: string
  DT_RowId?: string
}

export function companyEventsQuery(r: CompaniesTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'companies', 'events', r],
    queryFn: () => apiGet<TablePage<CompanyEvent>>(`/settings/companies/events?${companiesTableParams(r).toString()}`),
  })
}

/* -------------------------------------------------------------------------
 * Eligible for Adam Intop
 * ---------------------------------------------------------------------- */

export type AdamIntopState = {
  assigned: boolean
  /** Dreptul de scriere e altul decat cel de citire; serverul spune care e. */
  editable: boolean
}

export const adamIntopQuery = queryOptions({
  queryKey: ['settings', 'companies', 'adam-intop'],
  queryFn: () => apiGet<AdamIntopState>('/settings/companies/adam_intop'),
})

/** `on` / `off`, ca bifa din formularul vechi. */
export function saveAdamIntop(on: boolean) {
  return apiPost<{ saved: boolean; assigned: boolean }>('/settings/companies/adam_intop', { assign: on ? 'on' : 'off' })
}
